import React from 'react';
import type { ButtonProps } from '../../types';

interface ExtendedButtonProps extends Omit<ButtonProps, 'onClick'> {
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  'aria-label'?: string;
}

const Button: React.FC<ExtendedButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
  className = '',
  style = {},
  type = 'button',
  title,
  'aria-label': ariaLabel,
  ...props
}) => {
  const getVariantStyles = (): React.CSSProperties => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--accent)',
          color: 'white',
          border: 'none',
          ...style,
        };
      case 'secondary':
        return {
          backgroundColor: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border)',
          ...style,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: 'var(--text-primary)',
          border: '1px solid var(--border)',
          ...style,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: 'var(--text-primary)',
          border: 'none',
          // ...style,
        };
      case 'danger':
        return {
          backgroundColor: '#dc2626',
          color: 'white',
          border: 'none',
          ...style,
        };
      default:
        return style;
    }
  };

  const getSizeClasses = (): string => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md';
      case 'lg':
        return 'px-6 py-3 text-sm sm:text-base lg:text-lg font-medium rounded-lg';
      case 'ssm':
        return 'text-sm sm:text-base lg:text-lg font-medium rounded-lg';
      default:
        return 'px-4 py-2 text-sm sm:text-base font-medium rounded-md';
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;

    switch (variant) {
      case 'primary':
        e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
        break;
      case 'secondary':
        e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
        break;
      case 'outline':
        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
        break;
      case 'ghost':
        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
        break;
      case 'danger':
        e.currentTarget.style.backgroundColor = '#dc2626';
        break;
    }

    onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;

    const originalStyles = getVariantStyles();
    e.currentTarget.style.backgroundColor =
      originalStyles.backgroundColor || '';

    onMouseLeave?.(e);
  };

  return (
    <button
      type={type}
      className={`${getSizeClasses()} transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${className}`}
      style={
        {
          ...getVariantStyles(),
          '--tw-ring-color': 'var(--accent)',
          '--tw-ring-opacity': '0.3',
        } as React.CSSProperties
      }
      disabled={disabled || loading}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={title}
      aria-label={ariaLabel}
      {...props}
    >
      {loading ? (
        <div className="flex items-center gap-2">
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {children}
        </div>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
